import React from 'react';
import classNames from 'classnames';
import noop from '../utils/noop';
import Tooltip from '../tooltip';
import constants from './constants';
import getConstrainedRadii from './helpers/get-constrained-radii';

const { DRAGGABLE_BORDER_WIDTH } = constants;

const TOOLTIP_ID = 'draggable-coords';

class DraggableObserver extends React.Component {
  imageDragZone;

  static defaultProps = {
    borderWidth: DRAGGABLE_BORDER_WIDTH,
    onChange: noop,
    onBeforeChange: noop,
    onAfterChange: noop,
    onDoubleClick: noop,
    onOverlayClick: noop,
    tooltipText: null,
  };

  constructor(props) {
    super(props);
    this.state = {
      isDragged: false,
      startX: 0,
      startY: 0,
    };
    this.handleMouseDown = this.handleMouseDown.bind(this);
    this.handleMouseMove = this.handleMouseMove.bind(this);
    this.handleMouseUp = this.handleMouseUp.bind(this);
    this.handleMouseLeave = this.handleMouseLeave.bind(this);

    this.handleTouchStart = this.handleTouchStart.bind(this);
    this.handleTouchEnd = this.handleTouchEnd.bind(this);
    this.handleTouchMove = this.handleTouchMove.bind(this);
    this.stopDragging = this.stopDragging.bind(this);
    this.handleOverlayClick = this.handleOverlayClick.bind(this);
    this.imageDragZone = React.createRef();
  }

  componentDidMount() {
    document.body.addEventListener('mouseleave', this.handleMouseLeave);
  }

  componentWillUnmount() {
    document.body.removeEventListener('mouseleave', this.handleMouseLeave);
  }

  stopDragging(event) {
    const { onAfterChange } = this.props;
    const { isDragged } = this.state;
    this.setState({
      isDragged: false,
    });
    // @ts-ignore TS(2349): This expression is not callable.
    onAfterChange(event, isDragged);
  }

  handleTouchStart(event) {
    const [touch] = event.touches;
    const { clientX, clientY } = touch;

    const { onBeforeChange } = this.props;

    this.setState({
      isDragged: true,
      startX: clientX,
      startY: clientY,
    });

    // @ts-ignore TS(2349): This expression is not callable.
    onBeforeChange({ startX: clientX, startY: clientY }, event);
  }

  handleTouchMove(event) {
    event.stopPropagation();
    const { onChange } = this.props;
    const { startY, startX } = this.state;
    const [touch] = event.touches;
    const { clientX, clientY } = touch;
    const x = clientX - startX;
    const y = clientY - startY;

    // @ts-ignore TS(2349): This expression is not callable.
    onChange({ x, y }, event);
  }

  handleTouchEnd(event) {
    const { onAfterChange } = this.props;
    const { isDragged } = this.state;
    this.setState({
      isDragged: false,
    });

    // @ts-ignore TS(2349): This expression is not callable.
    onAfterChange(event, isDragged);
  }

  handleMouseDown(event) {
    const { onBeforeChange } = this.props;
    this.setState({
      isDragged: true,
      startX: event.clientX,
      startY: event.clientY,
    });
    // @ts-ignore TS(2349): This expression is not callable.
    onBeforeChange({ x: event.clientX, y: event.clientY }, event);
  }

  handleMouseMove(event) {
    event.stopPropagation();
    const { onChange } = this.props;
    const { startY, startX } = this.state;

    const x = event.clientX - startX;
    const y = event.clientY - startY;
    // @ts-ignore TS(2349): This expression is not callable.
    onChange({ x, y }, event);
  }

  handleMouseUp(event) {
    const { onAfterChange } = this.props;
    const { isDragged } = this.state;
    this.setState({
      isDragged: false,
    });

    // @ts-ignore TS(2349): This expression is not callable.
    onAfterChange(event, isDragged);
  }

  handleMouseLeave(event) {
    this.stopDragging(event);
  }

  handleOverlayClick(event) {
    const { onOverlayClick } = this.props;
    if (event.target !== this.imageDragZone.current) {
      // @ts-ignore TS(2349): This expression is not callable.
      onOverlayClick();
    }
  }

  render() {
    const { targetElementBounds, borderWidth, borderRadius, onDoubleClick, tooltipText } =
      this.props;

    if (!targetElementBounds) return null;

    const { isDragged } = this.state;
    const { width, height, top, bottom, right, left } = targetElementBounds;

    const adjustedRadii = getConstrainedRadii(borderRadius, width, height);

    const imageHighlighterStyles = {
      width,
      height,
      top,
      bottom,
      right,
      left,
      border: `${borderWidth}px solid #FFF`,
      borderTopLeftRadius: `${adjustedRadii.topLeft.horizontal}px ${adjustedRadii.topLeft.vertical}px`,
      borderTopRightRadius: `${adjustedRadii.topRight.horizontal}px ${adjustedRadii.topRight.vertical}px`,
      borderBottomRightRadius: `${adjustedRadii.bottomRight.horizontal}px ${adjustedRadii.bottomRight.vertical}px`,
      borderBottomLeftRadius: `${adjustedRadii.bottomLeft.horizontal}px ${adjustedRadii.bottomLeft.vertical}px`,
      boxSizing: 'border-box',
      pointerEvents: 'auto',
    };

    return (
      <div
        className="ws-drag-observer"
        // @ts-ignore TS(2322): Type '((event: any) => void) | null' is not assign... Remove this comment to see the full error message
        onMouseMove={isDragged ? this.handleMouseMove : null}
        onMouseUp={this.handleMouseUp}
        // @ts-ignore TS(2322): Type '((event: any) => void) | null' is not assign... Remove this comment to see the full error message
        onTouchMove={isDragged ? this.handleTouchMove : null}
        onDoubleClick={onDoubleClick}
        onClick={this.handleOverlayClick}
      >
        <div
          ref={this.imageDragZone}
          style={imageHighlighterStyles}
          className="ws-image-highlighter"
          onMouseDown={this.handleMouseDown}
          onTouchStart={this.handleTouchStart}
          onTouchEnd={this.handleTouchEnd}
          // @ts-ignore TS(2698): Spread types may only be created from object types... Remove this comment to see the full error message
          {...(tooltipText && {
            'data-tip': '',
            'data-for': TOOLTIP_ID,
          })}
        />
        {tooltipText && (
          <div
            className={classNames('ws-drag-observer__tooltip-container', {
              'ws-drag-observer__tooltip-container--visible': isDragged,
            })}
          >
            {/* @ts-ignore TS(2322): Type '{ children: never; id: string; effect: strin... Remove this comment to see the full error message */}
            <Tooltip id={TOOLTIP_ID} effect="float" place="bottom">
              {tooltipText}
            </Tooltip>
          </div>
        )}
      </div>
    );
  }
}

export default DraggableObserver;
